import { useState } from 'react'
import Toaster from '@ui/Toaster'
import { jsPDF } from 'jspdf'
import { toCanvas } from 'html-to-image'

type UseGetElementImageT = (fileName?: string) => UseGetElementImageReturnT

type ImageDataT = {
  base64Image: string
  width: number
  height: number
}

type UseGetElementImageReturnT = {
  error: Error | null
  isLoading: boolean
  downloadImage(node: HTMLElement | null, extension: ImageFormatE): Promise<void>
  getBase64ImageByElement(
    node: HTMLElement | null,
    extension: ImageFormatE
  ): Promise<ImageDataT | null>
}

type SavePdfParam = {
  imageData: string
  width: number
  height: number
}

export enum ImageFormatE {
  png = 'PNG',
  jpg = 'JPG',
  pdf = 'PDF',
}

const useGetElementImage: UseGetElementImageT = (fileName = 'image') => {
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const _savePdf = ({ imageData, width, height }: SavePdfParam) => {
    const pdf = new jsPDF({
      orientation: 'l',
      unit: 'px',
      compress: false,
      format: 'a2',
    })

    pdf.addImage({
      imageData,
      width,
      height,
      x: 0,
      y: 0,
      format: ImageFormatE.png,
      compression: 'NONE',
    })
    pdf.save(`${fileName}.pdf`)
    pdf.close()
  }

  const getBase64ImageByElement: UseGetElementImageReturnT['getBase64ImageByElement'] = async (
    node: HTMLElement | null,
    extension
  ) => {
    if (!node) return null

    try {
      const canvasNode = node.firstElementChild as HTMLElement
      // remove a broken floor plan image.
      canvasNode?.querySelectorAll<HTMLImageElement>('img').forEach((imgElement) => {
        if (imgElement.naturalHeight < 1 || imgElement.naturalWidth < 1) {
          imgElement?.remove()
        }
      })

      setIsLoading(true)
      const canvas = await toCanvas(canvasNode, {
        backgroundColor: '#fff',
      })
      const base64Image = canvas.toDataURL(imageFormatMediaTypeMap[extension], 1)

      // DOM purging
      canvas.remove()
      setIsLoading(false)

      return {
        base64Image,
        width: canvas.width,
        height: canvas.height,
      }
    } catch (error: any) {
      Toaster.fail({ error, message: error.message || error.target.toString() })
      setError(error)
    }

    return null
  }

  const downloadImage: UseGetElementImageReturnT['downloadImage'] = async (node, extension) => {
    const imageData = await getBase64ImageByElement(node, extension)
    const a = document.createElement('a')

    if (!imageData) return

    // PDF Downloading
    if (extension === ImageFormatE.pdf) {
      _savePdf({
        imageData: imageData.base64Image,
        width: imageData.width,
        height: imageData.height,
      })

      return
    }

    a.href = imageData.base64Image
    a.download = `${fileName}.${extension.toLocaleLowerCase()}`
    a.click()
    a.remove()
  }

  return {
    isLoading,
    downloadImage,
    getBase64ImageByElement,
    error,
  }
}

export default useGetElementImage

const imageFormatMediaTypeMap = {
  [ImageFormatE.png]: 'image/png',
  [ImageFormatE.pdf]: 'image/png',
  [ImageFormatE.jpg]: 'image/jpeg',
}
